"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.recurseZodObjectSchema = recurseZodObjectSchema;
const zod_1 = require("zod");
const utils_1 = require("./utils");
/**
 * Function goes through schema recursively and reconstructs it with ability to:
 * * modify individual schema field
 * * peek into individual schema field
 * * modify root and each recursive z.object
 *
 * @param schema - original zod schema
 * @param options - options depending on what needs to be done
 * @param options.peekFieldValue - check object fields that were recursively processed
 * @param options.adjustFieldValue - modify object fields that were recursively processed
 * @param options.adjustRecurseResult - modify entire z.object that was recursively processed
 *
 * @example
 * recurseZodObjectSchema(schema, {
 *     peekFieldValue: (fieldSchema) => {},             // peek
 *     adjustFieldValue: (fieldSchema) => fieldSchema,  // adjust single field
 *     adjustRecurseResult: (objSchema) => objSchema,   // adjust entire z.object
 * });
 * @example
 */
function recurseZodObjectSchema(schema, options) {
    return recurseZodObjectSchemaInternal(schema, options, { path: [] });
}
function recurseZodObjectSchemaInternal(schema, options, optionsInternal) {
    const shapeNew = {};
    for (const key in schema.shape) {
        const path = [...optionsInternal.path, key];
        const fieldDataOriginal = getSchemaForField(schema.shape[key]);
        const fieldSchemaOriginal = fieldDataOriginal.fieldSchema;
        if (fieldSchemaOriginal instanceof zod_1.z.ZodArray) {
            const arrayElementOriginal = getSchemaForField(fieldSchemaOriginal._def.type);
            if (arrayElementOriginal.fieldSchema instanceof zod_1.z.ZodObject) {
                const arrayElementSchema = recurseZodObjectSchemaInternal(arrayElementOriginal.fieldSchema, options, { key, path });
                shapeNew[key] = redecorateArray(zod_1.z.array(redecorate(arrayElementSchema, arrayElementOriginal)), fieldSchemaOriginal);
            }
            else {
                shapeNew[key] = fieldSchemaOriginal;
            }
        }
        else if (fieldSchemaOriginal instanceof zod_1.z.ZodRecord) {
            const recordElementOriginal = getSchemaForField(fieldSchemaOriginal.element);
            if (recordElementOriginal.fieldSchema instanceof zod_1.z.ZodObject) {
                const recordElementSchema = recurseZodObjectSchemaInternal(recordElementOriginal.fieldSchema, options, { key, path });
                shapeNew[key] = zod_1.z.record(fieldSchemaOriginal.keySchema, redecorate(recordElementSchema, recordElementOriginal));
            }
            else {
                shapeNew[key] = fieldSchemaOriginal;
            }
        }
        else if (fieldSchemaOriginal instanceof zod_1.z.ZodObject) {
            shapeNew[key] = recurseZodObjectSchemaInternal(fieldSchemaOriginal, options, { key, path });
        }
        else if (fieldSchemaOriginal instanceof zod_1.z.ZodIntersection) {
            throw new Error('intersection via .and() or .intersection() are not supported, use .merge() instead');
        }
        else {
            shapeNew[key] = fieldSchemaOriginal;
        }
        // field callbacks
        options?.peekFieldValue?.(shapeNew[key], { key, path });
        shapeNew[key] = options?.adjustFieldValue?.(shapeNew[key], { key, path }) ?? shapeNew[key];
        shapeNew[key] = redecorate(shapeNew[key], fieldDataOriginal);
    }
    const result = zod_1.z.object(shapeNew);
    return (options.adjustRecurseResult?.(result, { key: optionsInternal.key, path: optionsInternal.path }) ??
        result);
}
function redecorate(schemaNew, decoratorsOriginal) {
    let schema = schemaNew;
    if (decoratorsOriginal.isOptional) {
        schema = schema.optional();
    }
    if (decoratorsOriginal.isNullable) {
        schema = schema.nullable();
    }
    return schema;
}
function redecorateArray(arraySchemaNew, arraySchemaOriginal) {
    // redecorate - array specific
    if ((0, utils_1.isNumber)(arraySchemaOriginal._def.maxLength?.value)) {
        arraySchemaNew = arraySchemaNew.max(arraySchemaOriginal._def.maxLength.value, arraySchemaOriginal._def.maxLength.message);
    }
    if ((0, utils_1.isNumber)(arraySchemaOriginal._def.minLength?.value)) {
        arraySchemaNew = arraySchemaNew.min(arraySchemaOriginal._def.minLength.value, arraySchemaOriginal._def.minLength.message);
    }
    if ((0, utils_1.isNumber)(arraySchemaOriginal._def.exactLength?.value)) {
        arraySchemaNew = arraySchemaNew.length(arraySchemaOriginal._def.exactLength.value, arraySchemaOriginal._def.exactLength.message);
    }
    return arraySchemaNew;
}
function getSchemaForField(shape) {
    const resultDefault = {
        fieldSchema: shape,
        isOptional: false,
        isNullable: false,
    };
    if (shape instanceof zod_1.z.ZodOptional) {
        return shape._def.innerType instanceof zod_1.z.ZodNullable
            ? {
                fieldSchema: shape._def.innerType._def.innerType,
                isOptional: true,
                isNullable: true,
            }
            : {
                ...resultDefault,
                fieldSchema: shape._def.innerType,
                isOptional: true,
            };
    }
    if (shape instanceof zod_1.z.ZodNullable) {
        return shape._def.innerType instanceof zod_1.z.ZodOptional
            ? {
                fieldSchema: shape._def.innerType._def.innerType,
                isOptional: true,
                isNullable: true,
            }
            : {
                ...resultDefault,
                fieldSchema: shape._def.innerType,
                isNullable: true,
            };
    }
    return resultDefault;
}
